<template>
  <div class="tile my-4 px-4 py-4">
    <flow-group :flowGroup="flowGroup"/>
    <div class="px-3 py-4 my-4" :class="bgStatus">
      <div class="flex">
        <div class="mx-3">
          <flow-status :flow="this.userIntegrationFlow" has-icon/>
        </div>
        <p>{{ flowMessageText }}</p>
      </div>
    </div>
    <div class="flex justify-content-end mx-3">
      <manual-link-button :manual-link="manualLink"/>
      <form-button
        v-if="$can('flows.overview.update')"
        :value="flowButtonText"
        @click.native="handleChangeStatus"
        :processing="false"
      />
    </div>
  </div>
  <FlowScheduledRetry :user="user" :flowName="flowName" :readOnly="true"/>
  <update-billing-address-modal
    v-if="isUpdateBillingAddressModalOpen"
    :user="user"
    @close="() => (this.isUpdateBillingAddressModalOpen = false)"
    @valid="() => (this.hasValidBillingAddress = true)"
  ></update-billing-address-modal>
</template>

<script>
import {mapGetters} from 'vuex';
import DashboardLayout from '@/Layouts/DashboardLayout';
import IntegrationFlowLayout from '@/Layouts/IntegrationFlowLayout';
import Swal from 'sweetalert2';
import FlowGroup from '@/components/Dashboard/FlowGroup';
import FlowStatus from '@/components/Dashboard/FlowStatus';
import UpdateBillingAddressModal from '@/components/Dashboard/UpdateBillingAddressModal';
import FormButton from '@/components/Shared/Form/Button';
import ManualLinkButton from '@/components/Shared/ManualLinkButton.vue';
import FlowScheduledRetry from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowScheduledRetry.vue";

export default {
  name: 'FlowOverview',

  layout: [DashboardLayout, IntegrationFlowLayout],

  props: {
    user: Object,
    manualLink: String,
  },

  components: {
    FlowScheduledRetry,
    ManualLinkButton,
    FormButton,
    FlowGroup,
    FlowStatus,
    UpdateBillingAddressModal,
  },

  data() {
    return {
      isUpdateBillingAddressModalOpen: false,
      hasValidBillingAddress: false,
      flowName: '',
    };
  },

  beforeMount: function () {
    this.setFlowName();
  },
  mounted: function () {
    this.hasValidBillingAddress = this.user?.has_valid_billing_address;
  },

  computed: {
    ...mapGetters('flowsDetails', ['userIntegrationFlow', 'flowGroup', 'userIntegration']),

    flowMessageText() {
      let messageKey = (flag) => {
        switch (flag) {
          case 1:
            return 'dashboard.integrations.flows.overview.messages.paused'
          case 2:
            return 'dashboard.integrations.flows.overview.messages.stopped'
          case 3:
            return 'dashboard.integrations.flows.overview.messages.archived'
          default:
            return 'dashboard.integrations.flows.overview.messages.active'
        }
      }

      return this.trans(messageKey(this.userIntegrationFlow.flag))
    },

    bgStatus() {
      switch (this.userIntegrationFlow.flag) {
        case 0:
          return 'flow-status-success';
        case 1:
          return 'flow-status-paused';
        case 2:
          return 'flow-status-stopped';
      }
    },

    flowButtonText() {
      return this.userIntegrationFlow.flag === 0 ?
        this.trans('dashboard.integrations.flows.overview.actions.pause')
        : this.trans('dashboard.integrations.flows.overview.actions.resume');
    },
  },

  methods: {
    handleChangeStatus() {
      let isFlowRunning = this.userIntegrationFlow.flag === 0;
      let isIntegrationOnTrial = this.userIntegration.trial_left_in_days > 0;
      let isUserBillable = this.user.is_billable;
      let hasValidBillingAddress = this.user.has_valid_billing_address || this.hasValidBillingAddress;

      if (isUserBillable && !isIntegrationOnTrial && !isFlowRunning && !hasValidBillingAddress) {
        this.isUpdateBillingAddressModalOpen = true;

        return;
      }

      let text = this.trans(isFlowRunning ?
        'dashboard.integrations.flows.overview.deactivate_flow_confirmation_description'
        : 'dashboard.integrations.flows.overview.activate_flow_confirmation_description');

      let successText = this.trans(isFlowRunning ?
        'dashboard.alerts.success_deactivate_flow'
        : 'dashboard.alerts.success_activate_flow');

      Swal
        .fire({
          text: text,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          cancelButtonText: this.trans('common.cancel'),
          confirmButtonText: this.trans('common.confirm'),
        })
        .then(result => {
          if (result.value) {
            axios
              .patch(`/user/${this.user.id}/integration-flow/${this.userIntegrationFlow.id}/flag-update`, {
                flag: !this.userIntegrationFlow.flag,
              })
              .then(response => {
                if (response.data.subscribeViaCheckout !== true) {
                  this.userIntegrationFlow.flag = response.data.flag;
                  Swal.fire('', successText, 'success');

                  return;
                }

                Swal
                  .fire({
                    text: this.trans('dashboard.integrations.flows.overview.require_subscription_via_checkout_confirmation_description'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: this.trans('common.confirm'),
                  })
                  .then(action => {
                    if (action.value) {
                      window.location.href = response.data.checkoutUrl;
                    }
                  });
              });
          }
        });
    },

    setFlowName() {
      let name = this.userIntegrationFlow.first_flow.name

      let start = 4;
      let end = 0;
      if (name.substring(0, 3) === 'get') {
        start = 3
      }

      if (name.slice(-5) === 'Batch') {
        end = 5
      }

      this.flowName = name.substring(start, name.length - end);
    },
  },
};
</script>

<style lang="scss" scoped>
.flow-status-success {
  background-color: #edffed;
  border-width: 2px;
  border-color: #d2efd0;
}

.flow-status-paused {
  background-color: var(--warning);
  border: 1px solid transparent;
  color: var(--warning-color);
}

.flow-status-stopped {
  background-color: #ffedef;
  border-width: 2px;
  border-color: #efd0d0;
}
</style>
